/**
 * Email vocabulary, mirrored from the backend.
 *
 * The backend owns these names — `playaz4playaz-backend/enums/email.enum.ts` is the source of
 * truth, and the values here are byte-identical to it. Mirrored rather than fetched because the
 * Email Manager screens need them to render filter controls and status labels before any request
 * has come back, and a select whose options arrive late is a select that flickers.
 *
 * If a value changes on the backend it must change here in the same pull request. A drifted enum
 * does not fail loudly; it produces a filter that silently matches nothing.
 */

export const EMAIL_TEMPLATE_KEY = {
  WELCOME: 'welcome',
  EMAIL_VERIFICATION: 'emailVerification',
  PASSWORD_RESET: 'passwordReset',
  PASSWORD_CHANGED: 'passwordChanged',
  LOGIN_ALERT: 'loginAlert',
  SUBSCRIPTION_CONFIRMATION: 'subscriptionConfirmation',
  PAYMENT_FAILED: 'paymentFailed',
  BILLING_NOTICE: 'billingNotice',
  CAMPAIGN: 'campaign'
};

export const EMAIL_CATEGORY = {
  ONBOARDING: 'onboarding',
  SECURITY: 'security',
  BILLING: 'billing',
  ENGAGEMENT: 'engagement'
};

export const EMAIL_DELIVERY_CLASS = {
  TRANSACTIONAL: 'transactional',
  SCHEDULED: 'scheduled',
  CAMPAIGN: 'campaign'
};

export const EMAIL_VARIANT = {
  MOBILE: 'mobile',
  DESKTOP: 'desktop'
};

/**
 * Send outcomes.
 *
 * `DELIVERED`, `BOUNCED` and `COMPLAINED` are implemented on the backend — there are transports
 * that report them and a webhook that applies them — but they only start appearing once
 * `MAIL_PROVIDER` selects one of those transports. Until then `ACCEPTED` remains the strongest
 * outcome a send can reach, and it means "the transport took it", not "it arrived".
 *
 * That distinction is the whole reason the Delivery tab spells it out: an operator looking at
 * `accepted` needs to know whether it means "we have not heard back" or "it landed".
 */
export const SEND_OUTCOME = {
  QUEUED: 'queued',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
  FAILED: 'failed',
  SUPPRESSED: 'suppressed',
  SKIPPED: 'skipped',
  DELIVERED: 'delivered',
  BOUNCED: 'bounced',
  COMPLAINED: 'complained'
};

export const SKIP_REASON = {
  UNSUBSCRIBED: 'unsubscribed',
  UNVERIFIED: 'unverified',
  FATIGUE_CAP: 'fatigueCap',
  SUPPRESSED: 'suppressed',
  DUPLICATE: 'duplicate'
};

export const CAMPAIGN_STATUS = {
  DRAFT: 'draft',
  SCHEDULED: 'scheduled',
  SENDING: 'sending',
  SENT: 'sent',
  CANCELLED: 'cancelled',
  FAILED: 'failed',
  /*
    A series parent: it holds a cadence and never sends anything itself. Each occurrence is its own
    campaign row, linked back by `recurringParent`.

    There is deliberately no ARCHIVED here. Archiving is a timestamp on the campaign, so one that
    reached eleven thousand people still reads as `sent` after it is tidied away.
  */
  RECURRING: 'recurring'
};

/** Mirrors RecurrenceCadenceEnum. Three cadences, not an open rule set. */
export const RECURRENCE_CADENCE = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly'
};

export const SUPPRESSION_CAUSE = {
  UNSUBSCRIBE: 'unsubscribe',
  HARD_BOUNCE: 'hardBounce',
  COMPLAINT: 'complaint'
};

export const EMAIL_SEGMENT = {
  ENGAGED: 'engaged',
  CASUAL: 'casual',
  DORMANT: 'dormant'
};

export const EMAIL_STATUS_FILTER = {
  VERIFIED: 'verified',
  UNVERIFIED: 'unverified',
  ANY: 'any'
};

/**
 * The dashboard's Subscription filter, stored in a campaign's `subscriptionTiers`.
 *
 * Mirrors `SUBSCRIPTION_FILTER` in the backend's `api/email/lib/audienceMembership.ts`. It used to
 * offer whatever old-platform tier names the loaded page held, matched against a migrated user
 * field nothing current writes — so a fan subscribed through today's plans matched no tier at all.
 * The server now answers it from the fan's live subscriptions.
 */
export const SUBSCRIPTION_FILTER = {
  SUBSCRIBED: 'subscribed',
  NOT_SUBSCRIBED: 'notSubscribed'
};

export const SUBSCRIPTION_FILTER_LABEL = {
  [SUBSCRIPTION_FILTER.SUBSCRIBED]: 'Subscribed',
  [SUBSCRIPTION_FILTER.NOT_SUBSCRIBED]: 'Not subscribed'
};

/**
 * Personalization variables, in the client's own vocabulary.
 *
 * These labels are what an administrator sees and types — `{First Name}`, not `{{firstName}}`. The
 * backend maps them to template tokens on save, so the CMS never needs to know the token form.
 */
export const EMAIL_VARIABLE_LABELS = [
  'First Name',
  'Team Name',
  'Event Date',
  'Rank',
  'Next Action',
  'Timestamp',
  'Device Type',
  'Browser',
  'Approximate Location',
  'Country',
  'Plan Name',
  'Price',
  'Renewal Date',
  'Card Type',
  'Last4Digits'
];

/**
 * The variables a campaign may insert into its own two text fields.
 *
 * A subset of the list above, and deliberately a small one. Two rules decide it:
 *
 * 1. A campaign send must actually supply the value. The dispatcher supplies first name, team,
 *    rank, country, event date and next action; everything else belongs to a billing or security
 *    event and would never resolve here.
 * 2. The variable must carry a fallback. A chip lands *inside* a field's value, and the row removal
 *    that makes a value-less variable safe does not reach inside a value — so a no-fallback tag
 *    leaves "Claim your  reward" for any fan missing it. That rules out First Name, whose absence
 *    the greeting handles by collapsing as a whole.
 *
 * Offering more would mean offering a chip that can put a hole in a fan's sentence, which is the
 * defect this whole area exists to remove.
 */
/*
 * No Country. The client's Campaign Email design has no country, and QA reported one showing in a
 * campaign; it came from the dashboard board's sample tag row, not from the email. The backend's
 * campaign template dropped it too, so the Template editor stops offering it as well. Copy already
 * saved with {Country} still resolves at the send.
 */
export const CAMPAIGN_INSERTABLE_VARIABLES = ['Team Name', 'Rank'];

/**
 * The chips a detail row's value offers: the values the platform fills for each fan.
 *
 * Event Date and Next Action were offered too, and printed two Campaign Builder fields. The builder
 * no longer has them (an operator writes the event date and the next action as rows), so on a new
 * campaign both chips inserted a tag with nothing behind it and the row vanished from every email:
 * QA's "Action and Event Date do not work". The renderer still resolves both tags, so campaigns
 * saved with them keep their stored values.
 */
export const DETAIL_ROW_VARIABLES = [...CAMPAIGN_INSERTABLE_VARIABLES];

/** Design widths the preview pane renders. Must match the backend's compiled variants. */
export const EMAIL_PREVIEW_WIDTH = {
  MOBILE: 390,
  DESKTOP: 680
};

/**
 * Human labels for the values the Delivery tab prints.
 *
 * The API returns the enum value, which is fine for a filter and poor in a support table —
 * `fatigueCap` is legible but it is not English, and SC-011 asks that "my verification email never
 * arrived" be answerable in under two minutes by someone who has never read this codebase.
 *
 * Falls back to the raw value for anything not listed, so a new backend outcome shows up as itself
 * rather than as a blank cell.
 */
export const SEND_OUTCOME_LABEL = {
  [SEND_OUTCOME.QUEUED]: 'Queued',
  [SEND_OUTCOME.ACCEPTED]: 'Accepted by transport',
  [SEND_OUTCOME.DELIVERED]: 'Delivered',
  [SEND_OUTCOME.REJECTED]: 'Rejected',
  [SEND_OUTCOME.FAILED]: 'Failed',
  [SEND_OUTCOME.BOUNCED]: 'Bounced',
  [SEND_OUTCOME.COMPLAINED]: 'Marked as spam',
  [SEND_OUTCOME.SUPPRESSED]: 'Not sent — suppressed',
  [SEND_OUTCOME.SKIPPED]: 'Not sent — skipped'
};

export const SKIP_REASON_LABEL = {
  [SKIP_REASON.UNSUBSCRIBED]: 'Unsubscribed from this category',
  [SKIP_REASON.UNVERIFIED]: 'Address never verified',
  [SKIP_REASON.FATIGUE_CAP]: 'Hit the anti-fatigue cap',
  [SKIP_REASON.SUPPRESSED]: 'Address is suppressed',
  [SKIP_REASON.DUPLICATE]: 'This trigger already sent it'
};

/** Label for a value, or the value itself when it is one this build has not seen. */
export const labelFor = (map, value) => (value ? map[value] || value : null);

/**
 * Sortable columns, mirrored from `SORTABLE_FIELDS` in the backend's audience query.
 *
 * The value is the key the API accepts; the label is what an operator reads. `segment` is offered
 * because the dashboard lists it, and the backend answers it by sorting on `lastActiveAt` — which
 * is the field the segment is derived from, so the ordering is real rather than approximated.
 */
export const EMAIL_SORT_FIELDS = [
  { value: 'signupDate', label: 'Signup date' },
  { value: 'lastActive', label: 'Last active' },
  { value: 'country', label: 'Country' },
  // No Team: it is read per page from the fan's subscriptions and follows, so there is no stored
  // field to order the audience by. Ordering by the old one was ordering by nothing.
  { value: 'segment', label: 'Segment' }
];

/**
 * What a campaign is asking a fan to do, mirrored from `ConversionActionEnum` in the backend.
 *
 * The empty value is a real choice, not a placeholder prompt. A campaign with no conversion action
 * reports CTA Conversion as not measured rather than as 0% — "nobody converted" and "nobody said
 * what converting means" are different statements, and only one of them is about fans.
 */
/**
 * Where a campaign's primary action sends a fan, mirrored from `CampaignDestinationEnum` in the
 * backend. Values and order match that file exactly.
 *
 * `appBacked` is the half an operator most needs and would otherwise have to guess: a destination
 * the installed app opens directly, versus one that opens a browser for everyone. It is carried in
 * the data rather than written into the component, so the label cannot drift from the truth when
 * the app gains a route.
 *
 * The site root is deliberately absent. "The homepage, for every campaign" is the behaviour this
 * list replaces, so it must not be selectable either.
 *
 * `path` mirrors the backend catalog's, and is here because two things in this repository were
 * deriving it instead: the composer's address preview kept its own copy, which went stale, and
 * `appBackedClaims.test.js` guessed `/{value}/{target}` — a guess that held for every entry until
 * one arrived whose path is not named after it. A mirror that carries the address cannot drift
 * from the one being sent.
 */
export const CAMPAIGN_DESTINATIONS = [
  /*
    The feed entry points at the app-install page, and is not app-backed.

    `/fancam` was served by nothing — no web route, and matched by neither app-link declaration,
    both of which require a segment after `/fancam`. Campaigns' secondary links resolved to a 404.
    The install page is what a fan who cannot take part without the app actually needs.
  */
  {
    value: 'fancamFeed',
    label: 'Get the mobile app',
    path: '/get-the-mobile-app',
    appBacked: false,
    target: null
  },
  /*
    Named "FanCam Challenge", not "a specific FanCam".

    The old label is what made this entry look like something other than the challenge destination,
    to the point where the challenge appeared to be missing from the catalog entirely. One thing,
    one name, in the composer and on the reports screen alike.
  */
  /*
    The address is the picked entry, shown in its team's reel.

    It was `/fancam/:target` (one entry in a post viewer, with no way to take part), then the team's
    `fancam-challenge` action alone, which opens the Upload Photo/Video screen before the fan has seen
    a single entry. The backend fills `:team` from the picked entry at the send; `:target` is the
    entry, which the app plays in the team channel's full-screen reel.

    Still `fancam-challenge` first, because app builds without the reel link read the entry as an
    unused trailing segment of that action and open the upload screen as before. The web shows the
    channel with the FanCam Challenge app handoff for this address too.
  */
  {
    value: 'fancam',
    label: 'A FanCam Challenge',
    path: '/profile/:team/fancam-challenge/:target',
    appBacked: true,
    target: 'FanCam Challenge ID'
  },
  { value: 'post', label: 'A post', path: '/post/:target', appBacked: true, target: 'Post ID' },
  {
    value: 'profile',
    label: 'A fan profile',
    path: '/profile/:target',
    appBacked: true,
    target: 'Username'
  },
  { value: 'signup', label: 'The signup flow', path: '/signup', appBacked: true, target: null },
  { value: 'subscribe', label: 'Subscribe', path: '/subscribe', appBacked: false, target: null },
  {
    value: 'subscribeAthlete',
    label: 'Subscribe to an athlete',
    path: '/subscribe/:target',
    appBacked: false,
    target: 'Athlete ID'
  },
  { value: 'store', label: 'The store', path: '/store', appBacked: false, target: null },
  { value: 'news', label: 'News', path: '/news', appBacked: false, target: null },
  {
    value: 'getTheApp',
    label: 'Get the mobile app',
    path: '/get-the-mobile-app',
    appBacked: false,
    target: null
  },
  /*
    A team's rewards, which live in a section of that team's channel rather than on a page of their
    own — so the address names the channel and the section after it.

    Added because the campaign's secondary button says "See Rewards" and had nowhere to send
    anyone: it pointed at the app-install page, and on a phone it reached nothing at all, because
    the app claims the tracked-click path and had no screen to route a web-only destination to.
  */
  {
    value: 'rewards',
    label: "A team's rewards",
    path: '/profile/:target/rewards',
    appBacked: true,
    target: 'Team username'
  }
];

/** What a campaign points at when the operator chooses nothing. Mirrors the backend default. */
export const DEFAULT_CAMPAIGN_DESTINATION = 'fancamFeed';

/**
 * The destinations the composer offers in Phase 1 — which is not the same list as the one above.
 *
 * Two lists rather than a shorter one, and the distinction is the point. `CAMPAIGN_DESTINATIONS`
 * mirrors `CampaignDestinationEnum` exactly, and must keep doing so: the backend still accepts all
 * ten, and campaigns saved before this narrowing still carry values from it. Delete an entry there
 * and an existing campaign renders as the raw string `subscribeAthlete` with no resolvable address —
 * the operator is shown a broken campaign rather than one pointing somewhere no longer offered.
 *
 * So the narrowing happens here, over what may be *chosen*, while the mirror keeps answering what
 * may be *understood*.
 *
 * Phase 1 is one entry: `fancam`, the FanCam Challenge the campaign button carries a fan to.
 *
 * It is no longer true that "the feed is where the secondary link goes" — `/fancam` was served by
 * nothing, so that link landed every recipient on a 404. The secondary now goes to the app-install
 * page, which is the honest answer while no web feed exists.
 */
export const PHASE_1_CAMPAIGN_DESTINATIONS = ['fancam'];

/** Whether a stored destination is one an operator may still pick. */
export const isPhase1Destination = (value) => PHASE_1_CAMPAIGN_DESTINATIONS.includes(value);

/**
 * Where a campaign's secondary link goes.
 *
 * Still not an operator's choice — making it selectable would reopen the catalog Phase 1 exists to
 * narrow — but no longer a constant address either: rewards belong to a team, so the backend
 * addresses this at the campaign's own channel, and at the default team for the audience modes that
 * name no channel.
 *
 * It pointed at the app-install page, which is what "See Rewards" reached until this repair: no
 * rewards destination existed for it to reach.
 */
export const CAMPAIGN_SECONDARY_DESTINATION = 'rewards';

/**
 * Which destinations each conversion action can actually follow from.
 *
 * `rewardClaimed` mapped to nothing while nothing in the product led a fan to rewards. The team
 * channel's rewards section does, so the pairing is reachable and no longer warned on. The warning
 * machinery stays: an operator who picks an unreachable pairing deserves to know their
 * CTA Conversion can only ever read zero.
 *
 * `challengeEntered` is the entry that makes the table useful at all. Until it existed, every action
 * here mapped to `subscribe`, `subscribeAthlete` or `store` while Phase 1 offered only `fancam` — so
 * whatever an operator picked was unreachable, and CTA Conversion could not leave zero for any
 * campaign that could actually be sent.
 */
export const CONVERSION_ACTION_DESTINATIONS = {
  subscriptionStarted: ['subscribe', 'subscribeAthlete'],
  subscriptionUpgraded: ['subscribe', 'subscribeAthlete'],
  orderPlaced: ['store'],
  rewardClaimed: ['rewards'],
  challengeEntered: ['fancam']
};

export const CONVERSION_ACTIONS = [
  { value: '', label: 'No conversion action - CTA conversion stays unmeasured' },
  { value: 'subscriptionStarted', label: 'Starts a subscription' },
  { value: 'subscriptionUpgraded', label: 'Upgrades their subscription' },
  { value: 'orderPlaced', label: 'Places an order' },
  { value: 'rewardClaimed', label: 'Claims a reward' },
  { value: 'challengeEntered', label: 'Enters the FanCam Challenge' }
];

/**
 * The conversion actions the composer offers in Phase 1 — which is not the list above.
 *
 * Two lists for the same reason the destinations have two: the list above answers what may be
 * *understood*, and this one answers what may be *chosen*.
 *
 * Four of the five above are unreachable from the only destination Phase 1 offers. A fan sent to a
 * FanCam Challenge cannot start a subscription, upgrade one or place an order there, and no reward
 * screen exists anywhere to claim from. So choosing any of them is not a risk of a low number, it
 * is a guarantee of zero — and the operator finds out weeks later from a report.
 *
 * The narrowing happens here rather than by shortening `CONVERSION_ACTIONS`, because a campaign
 * saved before this still carries `orderPlaced` or `subscriptionStarted`. Shorten the list above
 * and a controlled `<select>` with no matching option renders blank, and the pairing warning prints
 * the raw stored key at an operator — a broken campaign shown in place of one whose choice is
 * merely no longer offered.
 *
 * The backend's `ConversionActionEnum` is untouched. All five still validate on write, and the
 * three call sites that record a subscription, an upgrade and an order keep recording them: what an
 * operator may pick is not a statement about what the platform observes.
 */
export const PHASE_1_CONVERSION_ACTIONS = ['challengeEntered'];

/** Whether a stored conversion action is one an operator may still pick. */
export const isPhase1ConversionAction = (value) => PHASE_1_CONVERSION_ACTIONS.includes(value);

/**
 * Which of the three ways a campaign names its audience is in force.
 *
 * Mirrors `AudienceModeEnum` in the backend's `enums/email.enum.ts`. Exactly one is active per
 * campaign: a saved segment, a channel audience and one named person all answer the same question
 * — who receives this — and a campaign carrying two of them would have two answers with nothing to
 * say which one sends.
 *
 * Absent on a campaign written before this feature, which reads as `savedSegment`.
 *
 * `DIRECT_RECIPIENT` is an audience of exactly one account. It exists so a campaign can be pointed
 * at a single real inbox on a real environment and watched end to end — and it is not the proof
 * send, which goes to the signed-in administrator, is marked `[TEST]` and is kept out of the
 * campaign's statistics. This one is an ordinary send with an audience of one.
 */
export const AUDIENCE_MODE = {
  SAVED_SEGMENT: 'savedSegment',
  CHANNEL_SPECIFIC: 'channelSpecific',
  DIRECT_RECIPIENT: 'directRecipient'
};

/**
 * How a fan is connected to the channel a campaign is aimed at.
 *
 * Mirrors `ChannelRelationshipScopeEnum`. The backend owns these values; this file owns only the
 * words beside them.
 */
export const CHANNEL_RELATIONSHIP_SCOPE = {
  FOLLOWERS: 'followers',
  SUBSCRIBERS: 'subscribers',
  ALL_CONNECTED: 'allConnected'
};

/**
 * What each scope is called on screen.
 *
 * `allConnected` deliberately says "fans", not "subscribers". An operator reading "All subscribers"
 * over an audience that is mostly followers would send commercial, member-only copy to people who
 * never paid for anything — which is the one misreading this scope makes possible.
 */
export const CHANNEL_SCOPE_LABEL = {
  [CHANNEL_RELATIONSHIP_SCOPE.FOLLOWERS]: 'Followers',
  [CHANNEL_RELATIONSHIP_SCOPE.SUBSCRIBERS]: 'Subscribers',
  [CHANNEL_RELATIONSHIP_SCOPE.ALL_CONNECTED]: 'All connected fans'
};

/** One line under each scope, so the difference is readable without guessing. */
export const CHANNEL_SCOPE_HINT = {
  [CHANNEL_RELATIONSHIP_SCOPE.FOLLOWERS]: 'Everyone following this channel.',
  [CHANNEL_RELATIONSHIP_SCOPE.SUBSCRIBERS]:
    'Only fans with a live subscription. Cancelled and lapsed subscriptions are excluded.',
  [CHANNEL_RELATIONSHIP_SCOPE.ALL_CONNECTED]:
    'Followers and active subscribers together. A fan who is both receives one email.'
};

export const CHANNEL_SCOPE_OPTIONS = Object.values(CHANNEL_RELATIONSHIP_SCOPE).map((value) => ({
  value,
  label: CHANNEL_SCOPE_LABEL[value],
  hint: CHANNEL_SCOPE_HINT[value]
}));

/**
 * Why an audience cannot be sent to, in the operator's words.
 *
 * Mirrors `AudienceBlockReasonEnum`. Four reasons rather than one, because each has a different
 * next step: widen the audience, pick another channel, pick another person, or try the count
 * again.
 */
export const AUDIENCE_BLOCK_REASON = {
  ZERO_ELIGIBLE_RECIPIENTS: 'zeroEligibleRecipients',
  CHANNEL_UNAVAILABLE: 'channelUnavailable',
  RECIPIENT_UNAVAILABLE: 'recipientUnavailable',
  COUNT_UNAVAILABLE: 'countUnavailable'
};

export const AUDIENCE_BLOCK_MESSAGE = {
  [AUDIENCE_BLOCK_REASON.ZERO_ELIGIBLE_RECIPIENTS]:
    'Nobody in this channel audience can be emailed right now, so there is nothing to send. Choose a wider relationship or another channel.',
  [AUDIENCE_BLOCK_REASON.CHANNEL_UNAVAILABLE]:
    'This channel can no longer be used for a campaign — it is archived, suspended, or no longer a channel. Choose another one.',
  [AUDIENCE_BLOCK_REASON.RECIPIENT_UNAVAILABLE]:
    'This account can no longer receive a campaign — it is gone, suspended, or has no email address. Choose another recipient.',
  [AUDIENCE_BLOCK_REASON.COUNT_UNAVAILABLE]:
    'The recipient count could not be worked out, so this audience cannot be verified. Try again before sending.'
};

/**
 * The same refusal, said about one person rather than about an audience.
 *
 * A separate table rather than a mode-aware sentence builder, because only one of the four
 * actually differs — and it differs completely. "Nobody in this channel audience can be emailed"
 * is, for an audience of one, both false and useless: the operator knows exactly who it is about
 * and needs to be told *why*, which for a single test account is almost always an address nobody
 * has verified yet.
 */
export const DIRECT_RECIPIENT_BLOCK_MESSAGE = {
  ...AUDIENCE_BLOCK_MESSAGE,
  [AUDIENCE_BLOCK_REASON.ZERO_ELIGIBLE_RECIPIENTS]:
    'This account cannot be emailed right now — most often its address has not been verified, or it has unsubscribed from engagement email. Verify the address or choose another account.'
};
